import {getTestProps} from '@benchling/js-test-utils';
import {
  ActionIcon,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  createStyles,
  getStylesRef,
} from '@mantine/core';
import React from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import InputChip from '../lib/InputChip';
import type {ComponentKind, PredictionComponent} from './newPredictionState';

interface ComponentRowProps {
  component: PredictionComponent;
  // When true, the Copies field is pinned/disabled at 1 (monomer models can't have copies).
  copiesDisabled: boolean;
  disabled?: boolean;
  isOnlyComponent: boolean;
  isTypeDisabled: boolean;
  label: string;
  // Upper bound for the Copies field (the model's max component count).
  maxCopies: number;
  moleculesSupported: boolean;
  onCopiesChange: (copies: number) => void;
  onRemove: () => void;
  onTypeChange: (type: ComponentKind) => void;
  onValueChange: (value: string) => void;
}

const COMPONENT_TYPE_SEGMENTS: Array<{label: string; value: ComponentKind}> = [
  {label: 'AA sequence', value: 'aminoAcids'},
  {label: 'Ligand', value: 'smiles'},
];

export default function ComponentRow({
  component,
  copiesDisabled,
  disabled = false,
  isOnlyComponent,
  isTypeDisabled,
  label,
  maxCopies,
  moleculesSupported,
  onCopiesChange,
  onRemove,
  onTypeChange,
  onValueChange,
}: ComponentRowProps): JSX.Element {
  const {classes} = useStyles({kind: component.type});
  const isLigand = component.type === 'smiles';
  // An entity's type and value come from the registry; only how many copies to submit is the user's.
  const isEntity = component.entity != null;
  const componentTypeSegments =
    moleculesSupported || isLigand
      ? COMPONENT_TYPE_SEGMENTS
      : COMPONENT_TYPE_SEGMENTS.filter((option) => option.value === 'aminoAcids');

  const handleValueChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onValueChange(event.currentTarget.value);
  };

  const handleTypeChange = (nextValue: string): void => {
    if (nextValue === 'aminoAcids' || nextValue === 'smiles') {
      onTypeChange(nextValue);
    }
  };

  const handleCopiesChange = (nextValue: number | ''): void => {
    onCopiesChange(typeof nextValue === 'number' && nextValue >= 1 ? Math.floor(nextValue) : 1);
  };

  return (
    <Paper
      aria-label={label}
      className={classes.card}
      p="md"
      radius="md"
      role="group"
      withBorder={true}
      {...getTestProps({component: 'ComponentRow', element: 'root', key: component.id})}
      data-amp-safe={false}
    >
      <Stack spacing="md">
        <Group noWrap={true} position="apart">
          <InputChip
            entityName={component.entity?.name}
            label={label}
            size="md"
            type={component.type}
            value={component.value}
          />
          <ActionIcon
            aria-label={`Remove ${label}`}
            className={classes.rowAction}
            disabled={disabled || isOnlyComponent}
            onClick={onRemove}
            variant="subtle"
          >
            <BenchlingIcon icon="trash-can-outline" />
          </ActionIcon>
        </Group>
        <Group align="center" className={classes.settingsRow} noWrap={true} position="apart" spacing="sm">
          <SegmentedControl
            aria-label={`${label} type`}
            data={componentTypeSegments}
            disabled={disabled || isTypeDisabled || isEntity}
            onChange={handleTypeChange}
            size="sm"
            value={component.type}
          />
          {!copiesDisabled && (
            <Group align="center" noWrap={true} spacing={6}>
              <Text color="dimmed" size="sm">
                Copies
              </Text>
              <NumberInput
                aria-label={`${label} copies`}
                disabled={disabled}
                max={maxCopies}
                min={1}
                onChange={handleCopiesChange}
                size="md"
                value={component.copies}
                w={100}
              />
            </Group>
          )}
        </Group>
        <Textarea
          aria-label={`${label} value`}
          autosize={true}
          classNames={{input: classes.rawInput}}
          disabled={disabled}
          minRows={4}
          onChange={handleValueChange}
          placeholder={isLigand ? 'SMILES' : 'Paste or type your protein sequence here…'}
          readOnly={isEntity}
          value={component.value}
        />
      </Stack>
    </Paper>
  );
}

const useStyles = createStyles((theme, {kind}: {kind: ComponentKind}) => ({
  card: {
    [`&:hover .${getStylesRef('componentRowAction')}, &:focus-within .${getStylesRef('componentRowAction')}`]:
      {
        opacity: 1,
        pointerEvents: 'auto',
      },
    borderLeft: `3px solid ${kind === 'smiles' ? theme.colors.grape[6] : theme.colors.blue[6]}`,
    // Fill the grid cell so cards in a row tile evenly with equal heights, matching the model card grid.
    height: '100%',
  },
  settingsRow: {
    minWidth: 0,
  },
  rawInput: {
    fontFamily: 'var(--font-mono, monospace)',
  },
  rowAction: {
    ref: getStylesRef('componentRowAction'),
    // Reveal on card hover/focus (see `.card` above) or keyboard focus. `pointerEvents` is toggled
    // with `opacity` so the hidden remove icon doesn't capture clicks near the card's right edge
    // (opacity alone leaves the button clickable); keyboard focus is unaffected.
    opacity: 0,
    pointerEvents: 'none',
    '&:focus-visible': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
}));
